import type { Area, Position, Size, Dot, MapCardCtaPosition } from './types';
import {
  DISCOUNT_AREAS,
  MAP_NOMINAL_HEIGHT,
  MAP_NOMINAL_WIDTH,
  NORMAL_AREAS,
  RESTRICTED_NOMINAL_AREAS,
} from './mapData';

const MAX_RANDOM_TRIES = 100;

const restrictedCtaArea = (
  ctaPosition: MapCardCtaPosition,
  drawingArea: Size
): Area => {
  const w = 0.7;
  const h = 80 / drawingArea.h;
  const x = (1 - w) / 2;
  const y = ctaPosition === 'middle' ? 0.55 - h / 2 : 1 - h;
  return { x, y, w, h };
};

const offsetArea = (area: Area, drawingArea: Size, offset: Position): Area => {
  /* With background-size: cover, the image is scaled to exactly cover
     the entire container, and the aspect ratio is preserved.
     We need to know this scaling factor to adjust the map areas proportionally
   */
  const minScaleX = drawingArea.w / MAP_NOMINAL_WIDTH;
  const minScaleY = drawingArea.h / MAP_NOMINAL_HEIGHT;
  const imageScale = Math.max(minScaleX, minScaleY);
  // Unless the component has the exact same aspect ratio as the map, the image will be cropped
  const viewableFractionX = minScaleX > minScaleY ? 1 : imageScale / minScaleX;
  const viewableFractionY = minScaleY > minScaleX ? 1 : imageScale / minScaleY;
  /* The original implementation added background-position (with percentages) to make the visible area
     match the UX specs for the initial use case. The areas need to be offset accordingly.
     See https://developer.mozilla.org/en-US/docs/Web/CSS/background-position#formal_definition
   */
  const bgOffsetX =
    ((drawingArea.w - MAP_NOMINAL_WIDTH * imageScale) * offset.x) /
    drawingArea.w;
  const bgOffsetY =
    ((drawingArea.h - MAP_NOMINAL_HEIGHT * imageScale) * offset.y) /
    drawingArea.h;
  return {
    x: area.x * viewableFractionX + bgOffsetX,
    y: area.y * viewableFractionY + bgOffsetY,
    w: area.w * viewableFractionX,
    h: area.h * viewableFractionY,
  };
};

// Dot spacing constraints
const NORMAL_DOT_BREATH_SPACE: Area = { x: -0.07, y: -0.05, w: 0.14, h: 0.1 };
const DISCOUNT_DOT_BREATH_SPACE: Area = { x: -0.19, y: -0.21, w: 0.37, h: 0.4 };

// Generate a dynamic seed based on the current time
const getMapSeed = (): string => Math.floor(Date.now() / 1000).toString();

// Create a seeded random number generator
export const createRandom = (seed: string) => {
  let seedValue = Array.from(seed)
    .map((char) => char.charCodeAt(0))
    .reduce((acc, charCode) => acc + charCode, 0);

  return () => {
    const randomValue = Math.sin(seedValue++) * 10000;
    return randomValue - Math.floor(randomValue);
  };
};

// Check if a point is within a given area
export const isInArea = (x: number, y: number, area: Area) =>
  x >= area.x && y >= area.y && x <= area.x + area.w && y <= area.y + area.h;

// Check if a point is within any restricted area
export const isInRestrictedArea = (x: number, y: number, areas: Area[]) =>
  areas.some((area) => isInArea(x, y, area));

// Generate dots while avoiding restricted areas
export const generateDots = (
  random: () => number,
  area: Area,
  nDots: number,
  restrictedAreas: Area[],
  dotBreathSpace: Area
) => {
  const dots: Dot[] = [];
  const updatedRestrictedAreas = [...restrictedAreas];

  for (let i = 0; i < nDots; i++) {
    for (let t = 0; t < MAX_RANDOM_TRIES; t++) {
      const x = area.x + random() * area.w;
      const y = area.y + random() * area.h;

      if (!isInRestrictedArea(x, y, updatedRestrictedAreas)) {
        dots.push({ x, y });
        updatedRestrictedAreas.push({
          x: x + dotBreathSpace.x,
          y: y + dotBreathSpace.y,
          w: dotBreathSpace.w,
          h: dotBreathSpace.h,
        });
        break;
      }
    }
  }
  return { dots, restrictedAreas: updatedRestrictedAreas };
};

// Assign random discounts to dots
export const addDiscount = (
  dots: Dot[],
  minDiscount: number,
  maxDiscount: number,
  random: () => number
) => {
  dots.forEach((dot) => {
    dot.discount = Math.round(
      minDiscount + random() * (maxDiscount - minDiscount)
    );
  });
};

interface RegenerateAllDotsArgs {
  ctaPosition: MapCardCtaPosition;
  renderingOffset: Position;
  drawingArea: Size;
  seedOverride?: string;
}

// Generate all dots with proper spacing and restrictions
export const regenerateAllDots = ({
  ctaPosition,
  renderingOffset,
  drawingArea,
  seedOverride,
}: RegenerateAllDotsArgs) => {
  const seed = seedOverride ?? getMapSeed();
  const random = createRandom(seed);
  const adjustArea = (area: Area) =>
    offsetArea(area, drawingArea, renderingOffset);

  let restrictedAreas = [
    ...RESTRICTED_NOMINAL_AREAS.map(adjustArea),
    restrictedCtaArea(ctaPosition, drawingArea),
  ];
  // Generate discount dots
  const dealDots1 = generateDots(
    random,
    DISCOUNT_AREAS.discountDots1,
    2,
    restrictedAreas,
    DISCOUNT_DOT_BREATH_SPACE
  );
  addDiscount(dealDots1.dots, 20, 21, random);
  restrictedAreas = dealDots1.restrictedAreas;

  const dealDots2 = generateDots(
    random,
    DISCOUNT_AREAS.discountDots1,
    1,
    restrictedAreas,
    DISCOUNT_DOT_BREATH_SPACE
  );
  addDiscount(dealDots2.dots, 12, 19, random);
  restrictedAreas = dealDots2.restrictedAreas;

  // Generate normal dots
  const normalDots1 = generateDots(
    random,
    NORMAL_AREAS.normalDots1,
    6,
    restrictedAreas,
    NORMAL_DOT_BREATH_SPACE
  );
  restrictedAreas = normalDots1.restrictedAreas;

  return {
    dots: [...dealDots1.dots, ...dealDots2.dots, ...normalDots1.dots],
    restrictedAreas,
    discountAreas: DISCOUNT_AREAS,
    normalAreas: NORMAL_AREAS,
  };
};
